import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Venue, UserProfile, Publication, fetchVenues, toggleVisit, fetchPublications, isAdminUser } from '../services/api';
import VenueList from './VenueList';
import Analytics from '../services/analytics';
import SearchBar from './SearchBar';
import { Martini } from 'lucide-react';
import { ComingSoonPanel, ComingSoonTag, BARS_COMING_SOON } from './ComingSoon';
import { SortOption, SORT_LABELS, sortVenues } from '../utils/ratings';

interface VenuePageProps {
  userProfile: UserProfile | null;
  setUserProfile: React.Dispatch<React.SetStateAction<UserProfile | null>>;
}

const VenuePage: React.FC<VenuePageProps> = ({ userProfile, setUserProfile }) => {
  const { city } = useParams<{ city: string }>();
  const [venues, setVenues] = useState<Venue[]>([]);
  const [activeTab, setActiveTab] = useState<'restaurants' | 'bars'>('restaurants');
  const [publications, setPublications] = useState<Publication[]>([]);
  const [selectedPublication, setSelectedPublication] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('default');


  const loadVenues = useCallback(async () => {
    if (city) {
      try {
        const cityVenues = await fetchVenues(selectedPublication, selectedYear);
        setVenues(cityVenues.filter(venue => venue.city.toLowerCase() === city.toLowerCase()));
      } catch (error) {
        console.error('Error fetching venues:', error);
      }
    }
  }, [city, selectedPublication, selectedYear]);

  useEffect(() => {
    loadVenues();
  }, [loadVenues]);

  useEffect(() => {
    const loadPublications = async () => {
      try {
        const fetchedPublications = await fetchPublications();
        setPublications(fetchedPublications);
      } catch (error) {
        console.error('Error fetching publications:', error);
      }
    };
    loadPublications();
  }, []);

  const handleVisitToggle = async (venueId: number, date: Date | null) => {
    try {
      const updatedProfile = await toggleVisit(venueId, date);
      setUserProfile(updatedProfile);
    } catch (error) {
      console.error('Error toggling visit:', error);
    }
  };

  const filteredVenues = useMemo(() => {
    return venues.filter(venue => 
      (activeTab === 'restaurants' ? venue.venue_type === 'RESTAURANT' : venue.venue_type === 'BAR') &&
      venue.yearly_ratings.some(rating => 
        (!selectedPublication || rating.publication.name === selectedPublication) &&
        rating.year === selectedYear
      ) &&
      venue.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [venues, activeTab, selectedPublication, selectedYear, searchQuery]);

  // Sorted separately from filtering so changing the sort does not re-run the filter.
  const visibleVenues = useMemo(
    () => sortVenues(filteredVenues, sortBy, selectedYear),
    [filteredVenues, sortBy, selectedYear]
  );

  const filteredPublications = useMemo(() => {
    return publications.filter(pub => 
      pub.venue_type === 'BOTH' || 
      (activeTab === 'restaurants' && pub.venue_type === 'RESTAURANT') ||
      (activeTab === 'bars' && pub.venue_type === 'BAR')
    );
  }, [publications, activeTab]);

  const handleTabChange = (tab: 'restaurants' | 'bars') => {
    Analytics.logEngagement.filter('venue_type', tab);
    setActiveTab(tab);
  };
  
  const handlePublicationChange = (publicationName: string) => {
    Analytics.logEngagement.filter('publication', publicationName);
    setSelectedPublication(publicationName);
  };
  
  const handleYearChange = (year: number) => {
    Analytics.logEngagement.filter('year', year.toString());
    setSelectedYear(year);
  };

  const handleSortChange = (option: SortOption) => {
    Analytics.logEngagement.filter('sort', option);
    setSortBy(option);
  };

  const barsComingSoon = BARS_COMING_SOON && activeTab === 'bars';

  return (
    <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8 max-w-full overflow-hidden">
      <h2 className="font-display font-normal tracking-tight text-xl sm:text-2xl mb-4 sm:mb-6 px-2">Top Venues in {city}</h2>
      
      {activeTab === 'restaurants' && isAdminUser(userProfile) && (
        <Link to={`/dashboard/admin/map?city=${encodeURIComponent(city || '')}&q=${encodeURIComponent(searchQuery)}`} className="inline-flex mb-4 rounded-lg bg-brand-50 text-brand-700 px-4 py-2 text-sm font-medium">
          Explore Michelin map · Admin only
        </Link>
      )}
      {activeTab === 'restaurants' && (
        <div className="mb-6 rounded-xl border border-brand-200 bg-brand-50 p-4 flex flex-wrap items-center justify-between gap-3 text-sm">
          <p>Keep an eye on your favourites. Get email alerts for up to 5 restaurants.</p>
          <Link to="/alerts" className="font-medium text-brand-700 underline underline-offset-4">
            {userProfile?.is_pro ? `Manage alerts · ${userProfile.alert_venue_ids?.length ?? 0}/5` : 'Explore Pro alerts'}
          </Link>
        </div>
      )}
      {/* Searching and filtering do nothing against an unreleased section, so
          they are hidden while the coming-soon panel is showing. */}
      {!barsComingSoon && (
        <>
          {/* Mobile-only search bar */}
          <div className="md:hidden mb-4">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={`Search ${activeTab.toLowerCase()} in ${city}...`}
              className="w-full"
            />
          </div>

          {/* Mobile Filter Button */}
          <button
            className="md:hidden w-full mb-4 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors"
            onClick={() => setIsFilterOpen(!isFilterOpen)}
          >
            {isFilterOpen ? 'Hide Filters' : 'Show Filters'}
          </button>
        </>
      )}

      {/* Desktop layout and Mobile expanded filters. The tab group stays
          visible when bars are coming soon so you can switch back. */}
      <div className={`flex flex-col md:flex-row md:justify-between md:items-center mb-4 sm:mb-8 ${
        barsComingSoon ? 'flex' : (isFilterOpen ? 'block' : 'hidden md:flex')
      }`}>
        <div className="inline-flex rounded-md shadow-sm mb-4 md:mb-0" role="group">
          <button
            onClick={() => handleTabChange('restaurants')}
            className={`px-4 py-2 text-sm font-medium rounded-l-lg flex-1 md:flex-none ${
              activeTab === 'restaurants'
                ? 'bg-brand-600 text-white'
                : 'bg-surface text-ink-700 hover:bg-ink-50'
            }`}
          >
            Restaurants
          </button>
          <button
            onClick={() => handleTabChange('bars')}
            className={`px-4 py-2 text-sm font-medium rounded-r-lg flex-1 md:flex-none
                        inline-flex items-center justify-center gap-1.5 ${
              activeTab === 'bars'
                ? 'bg-brand-600 text-white'
                : 'bg-surface text-ink-700 hover:bg-ink-50'
            }`}
          >
            Bars
            {BARS_COMING_SOON && (
              <ComingSoonTag
                className={activeTab === 'bars' ? 'bg-surface/20 text-white ring-white/30' : ''}
              />
            )}
          </button>
        </div>
        
        <div className={`flex-col sm:flex-row gap-2 sm:space-x-4 ${barsComingSoon ? 'hidden' : 'flex'}`}>
          {/* Desktop-only search bar */}
          <div className="hidden md:block">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              className="w-64"
            />
          </div>
  
          <select
            value={selectedPublication}
            onChange={(e) => handlePublicationChange(e.target.value)}
            className="px-3 py-2 border rounded-md text-sm w-full sm:w-auto"
          >
            <option value="">All Publications</option>
            {filteredPublications.map((pub) => (
              <option key={pub.id} value={pub.name}>
                {pub.name}
              </option>
            ))}
          </select>
          
          <select
            value={selectedYear}
            onChange={(e) => handleYearChange(Number(e.target.value))}
            className="px-3 py-2 border rounded-md text-sm w-full sm:w-auto"
          >
            {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>

          <select
            value={sortBy}
            onChange={(e) => handleSortChange(e.target.value as SortOption)}
            className="px-3 py-2 border rounded-md text-sm w-full sm:w-auto"
            aria-label="Sort venues"
          >
            {(Object.keys(SORT_LABELS) as SortOption[]).map((option) => (
              <option key={option} value={option}>
                {SORT_LABELS[option]}
              </option>
            ))}
          </select>
        </div>
      </div>
  
      {barsComingSoon ? (
        <ComingSoonPanel
          icon={<Martini className="w-7 h-7" />}
          title="Top Bars are coming soon"
          description="We're building the bar guide the same way as the restaurants - ranked from
                       the lists worth trusting, with live availability wherever we can get it."
          footnote={`Restaurants in ${city} are live now`}
        />
      ) : (
        <>
          {/* Results count */}
          <div className="px-2 mb-4 text-sm text-ink-600">
            {filteredVenues.length} {filteredVenues.length === 1 ?
              `${activeTab.slice(0, -1)} found` :
              `${activeTab} found`}
          </div>

          <VenueList
            venues={visibleVenues}
            userProfile={userProfile}
            onVisitToggle={handleVisitToggle}
            selectedYear={selectedYear}
            setUserProfile={setUserProfile}
          />
        </>
      )}
    </div>
  );
}
export default VenuePage;
